/**
 * Image Analyzer - Analyzes images for color properties
 *
 * This module provides functionality to determine if an image is grayscale/black and white,
 * and can be used to prevent applying colorization to already colored images.
 */
const ImageAnalyzer = {
  /**
   * Analyzes an image to determine if it's grayscale (black & white)
   *
   * @param {fabric.Image} image - The fabric.js image object to analyze
   * @param {Object} options - Analysis options
   * @param {number} options.tolerance - Color difference tolerance (default: 5)
   * @param {number} options.sampleSize - Percentage of pixels to sample (default: 0.1 = 10%)
   * @param {number} options.colorThreshold - Threshold to consider image colored (default: 0.05 = 5%)
   * @param {boolean} options.fastMode - Use faster but less accurate algorithm (default: false)
   * @returns {Object} Analysis results
   */
  isGrayscale(image, options = {}) {
    // Default options
    const config = {
      tolerance: options.tolerance !== undefined ? options.tolerance : 5,
      sampleSize: options.sampleSize !== undefined ? options.sampleSize : 0.1,
      colorThreshold:
        options.colorThreshold !== undefined ? options.colorThreshold : 0.05,
      fastMode: options.fastMode !== undefined ? options.fastMode : false,
    };

    // Create a temporary canvas to analyze the image
    const tempCanvas = document.createElement('canvas');
    const ctx = tempCanvas.getContext('2d', { willReadFrequently: true });

    // Set canvas dimensions to match the image's natural dimensions
    tempCanvas.width = image.width * image.scaleX;
    tempCanvas.height = image.height * image.scaleY;

    // Draw the fabric image to the temporary canvas
    image.render(ctx);

    // Get the pixel data
    const imageData = ctx.getImageData(
      0,
      0,
      tempCanvas.width,
      tempCanvas.height
    );
    const data = imageData.data;

    // Faster algorithm for low-quality check
    if (config.fastMode) {
      return this._fastGrayscaleCheck(data, config);
    }

    // Full analysis
    return this._fullGrayscaleAnalysis(data, config);
  },

  /**
   * Fast algorithm to check if an image is grayscale
   * Samples a subset of pixels and checks if R=G=B within tolerance
   *
   * @private
   */
  _fastGrayscaleCheck(data, config) {
    const totalPixels = data.length / 4;
    const samplesToCheck = Math.max(
      100,
      Math.floor(totalPixels * config.sampleSize)
    );
    const pixelStep = Math.floor(totalPixels / samplesToCheck);

    let nonGrayscalePixels = 0;

    // Sample pixels at regular intervals
    for (let i = 0; i < totalPixels; i += pixelStep) {
      const index = i * 4;
      const r = data[index];
      const g = data[index + 1];
      const b = data[index + 2];

      // Check if the RGB values differ by more than the tolerance
      if (
        Math.abs(r - g) > config.tolerance ||
        Math.abs(r - b) > config.tolerance ||
        Math.abs(g - b) > config.tolerance
      ) {
        nonGrayscalePixels++;
      }
    }

    // Calculate the percentage of non-grayscale pixels
    const coloredPercentage = nonGrayscalePixels / samplesToCheck;

    // Return analysis results
    return {
      isGrayscale: coloredPercentage < config.colorThreshold,
      coloredPercentage: coloredPercentage,
      confidence: 1 - (config.sampleSize < 0.5 ? 0.5 : config.sampleSize), // Lower confidence for small samples
    };
  },

  /**
   * Thorough analysis of an image to determine if it's grayscale
   * Uses multiple techniques and provides detailed results
   *
   * @private
   */
  _fullGrayscaleAnalysis(data, config) {
    const totalPixels = data.length / 4;

    // Stats for analysis
    let nonGrayscalePixels = 0;
    let saturationSum = 0;
    let brightnessSum = 0;
    let histogram = { r: {}, g: {}, b: {} };

    // Color spread measures
    let minR = 255,
      maxR = 0,
      minG = 255,
      maxG = 0,
      minB = 255,
      maxB = 0;
    let colorfulness = 0;

    // Determine the step size for sampling
    const pixelStep = Math.max(1, Math.floor(1 / config.sampleSize));

    // Analyze the image
    for (let i = 0; i < data.length; i += 4 * pixelStep) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      // Update color ranges
      minR = Math.min(minR, r);
      maxR = Math.max(maxR, r);
      minG = Math.min(minG, g);
      maxG = Math.max(maxG, g);
      minB = Math.min(minB, b);
      maxB = Math.max(maxB, b);

      // Convert to HSB
      const hsb = this._rgbToHsb(r, g, b);
      saturationSum += hsb.s;
      brightnessSum += hsb.b;

      // Update histograms
      histogram.r[r] = (histogram.r[r] || 0) + 1;
      histogram.g[g] = (histogram.g[g] || 0) + 1;
      histogram.b[b] = (histogram.b[b] || 0) + 1;

      // Calculate difference between channels
      const rg = Math.abs(r - g);
      const rb = Math.abs(r - b);
      const gb = Math.abs(g - b);

      // Local colorfulness measure
      colorfulness += (rg + rb + gb) / 3;

      // Check if the pixel is not grayscale
      if (
        rg > config.tolerance ||
        rb > config.tolerance ||
        gb > config.tolerance
      ) {
        nonGrayscalePixels++;
      }
    }

    // Calculate metrics
    const sampledPixels = Math.ceil(totalPixels / pixelStep);
    const coloredPercentage = nonGrayscalePixels / sampledPixels;
    const avgSaturation = saturationSum / sampledPixels;
    const avgBrightness = brightnessSum / sampledPixels;
    const avgColorfulness = colorfulness / sampledPixels;

    // Calculate histogram similarity between channels
    const histSimilarity = this._calculateHistogramSimilarity(histogram);

    // Analyze color ranges to check for monochrome tinting
    const rangeR = maxR - minR;
    const rangeG = maxG - minG;
    const rangeB = maxB - minB;
    const rangeSimilarity =
      1 -
      (Math.max(rangeR, rangeG, rangeB) - Math.min(rangeR, rangeG, rangeB)) /
        255;

    // Weighted decision based on multiple factors
    const isGrayscaleByColor = coloredPercentage < config.colorThreshold;
    const isGrayscaleBySaturation = avgSaturation < 0.1; // Less than 10% average saturation
    const isGrayscaleByHistogram = histSimilarity > 0.9; // Over 90% similarity in color distributions

    // Calculate overall confidence
    const confidence =
      (isGrayscaleByColor ? 0.5 : 0) +
      (isGrayscaleBySaturation ? 0.3 : 0) +
      (isGrayscaleByHistogram ? 0.2 : 0);

    // Final decision - consider it grayscale if at least two methods agree
    const isGrayscale =
      (isGrayscaleByColor && isGrayscaleBySaturation) ||
      (isGrayscaleByColor && isGrayscaleByHistogram) ||
      (isGrayscaleBySaturation && isGrayscaleByHistogram);

    // Return detailed analysis
    return {
      isGrayscale,
      confidence,
      metrics: {
        coloredPercentage,
        avgSaturation,
        avgBrightness,
        histogramSimilarity: histSimilarity,
        colorfulness: avgColorfulness,
        colorRangeSimilarity: rangeSimilarity,
      },
      decision: {
        byColorDiff: isGrayscaleByColor,
        bySaturation: isGrayscaleBySaturation,
        byHistogram: isGrayscaleByHistogram,
      },
    };
  },

  /**
   * Convert RGB to HSB (HSV) color space
   * @private
   */
  _rgbToHsb(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    let s = max === 0 ? 0 : delta / max;
    let v = max;

    if (delta !== 0) {
      if (max === r) {
        h = ((g - b) / delta) % 6;
      } else if (max === g) {
        h = (b - r) / delta + 2;
      } else {
        h = (r - g) / delta + 4;
      }

      h *= 60;
      if (h < 0) h += 360;
    }

    return { h, s, b: v };
  },

  /**
   * Calculate similarity between color channel histograms
   * @private
   */
  _calculateHistogramSimilarity(histogram) {
    // Convert histograms to arrays and normalize
    const rHist = this._normalizeHistogram(histogram.r);
    const gHist = this._normalizeHistogram(histogram.g);
    const bHist = this._normalizeHistogram(histogram.b);

    // Calculate cosine similarity between histograms
    const rgSimilarity = this._cosineSimilarity(rHist, gHist);
    const rbSimilarity = this._cosineSimilarity(rHist, bHist);
    const gbSimilarity = this._cosineSimilarity(gHist, bHist);

    // Return average similarity
    return (rgSimilarity + rbSimilarity + gbSimilarity) / 3;
  },

  /**
   * Normalize a histogram to a fixed-length array
   * @private
   */
  _normalizeHistogram(hist) {
    const result = new Array(256).fill(0);
    let sum = 0;

    // Calculate sum
    for (const key in hist) {
      sum += hist[key];
    }

    // Normalize values
    for (const key in hist) {
      result[key] = hist[key] / sum;
    }

    return result;
  },

  /**
   * Calculate cosine similarity between two vectors
   * @private
   */
  _cosineSimilarity(a, b) {
    let dotProduct = 0;
    let aMagnitude = 0;
    let bMagnitude = 0;

    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      aMagnitude += a[i] * a[i];
      bMagnitude += b[i] * b[i];
    }

    aMagnitude = Math.sqrt(aMagnitude);
    bMagnitude = Math.sqrt(bMagnitude);

    // Avoid division by zero
    if (aMagnitude === 0 || bMagnitude === 0) {
      return 0;
    }

    return dotProduct / (aMagnitude * bMagnitude);
  },
};

export default ImageAnalyzer;
